// Live-data store + feed orchestrator.
//
// The Feed owns the whole data lifecycle: load initial REST snapshots, open the SSE stream,
// apply revisioned patches, reconnect with backoff, and re-fetch snapshots on `resync` /
// revision gaps. A slow or dropped browser connection is surfaced as a distinct
// browser-health state (feedStatus) — separate from any terminal/collector health.

import * as api from '../api/client';
import { SseClient, type SseFrame } from '../api/sse';
import type { StreamEvent, SseEventType } from '../types/api';
import { dampenTradingVpsStatuses, type GuardStatusDampingLedger } from '../lib/tradingVps';
import { Store } from './store';
import {
  applyEvent,
  initialFeedState,
  snapshotPatch,
  type FeedState,
  type FeedStatus,
} from './reducer';

/** The SSE endpoint (contracts/dashboard-api.md: `GET /api/v1/stream`; nginx proxies it
 *  with buffering off — see the dedicated `location /api/v1/stream` block). */
const STREAM_URL = '/api/v1/stream';

/** Steady-state trading-VPS poll cadence (matches the guard's one-second heartbeat). */
const TRADING_VPS_POLL_MS = 1000;
/** Watchdog: a /trading-vps request that has not settled by then is aborted and counted
 *  as a failure — a hung fetch must never freeze the poll chain on stale LIVE badges. */
const TRADING_VPS_TIMEOUT_MS = 8000;
/** Failure backoff cap. Consecutive failures double the retry delay up to this. */
const TRADING_VPS_MAX_BACKOFF_MS = 15000;

export const dataStore = new Store<FeedState>(initialFeedState);

/** Current best estimate of server time in epoch ms. */
export function serverNow(): number {
  return Date.now() + dataStore.get().clockOffsetMs;
}

const STREAM_EVENT_TYPES: readonly SseEventType[] = [
  'account_upsert',
  'account_remove',
  'fleet_summary',
  'alert',
  'health',
  'resync',
];

function decodeFrame(frame: SseFrame): StreamEvent | null {
  const type = (frame.event ?? '') as SseEventType;
  if (!STREAM_EVENT_TYPES.includes(type)) return null;
  const revision = frame.id != null ? Number(frame.id) : NaN;
  let data: unknown = undefined;
  if (frame.data) {
    try {
      data = JSON.parse(frame.data);
    } catch {
      return null;
    }
  }
  if (type === 'resync') {
    return { type: 'resync', revision: Number.isFinite(revision) ? revision : 0, data };
  }
  if (!Number.isFinite(revision)) return null;
  return { type, revision, data } as StreamEvent;
}

export class Feed {
  private sse: SseClient | null = null;
  private running = false;
  private snapshotToken = 0;
  private offlineTimer: ReturnType<typeof setTimeout> | null = null;
  private tradingVpsTimer: ReturnType<typeof setTimeout> | null = null;
  private tradingVpsPollToken = 0;
  private tradingVpsFailures = 0;
  private tradingVpsDamping: GuardStatusDampingLedger = new Map();
  /** Callback invoked when the server rejects the stream with 401 (session expired). */
  onAuthExpired: (() => void) | null = null;

  /** Begin the feed: load snapshots, then open the stream. Idempotent. */
  async start(): Promise<void> {
    if (this.running) return;
    this.running = true;
    dataStore.set({ feedStatus: 'connecting' });
    void this.pollTradingVps();
    await this.loadSnapshots(true);
    if (!this.running) return;
    this.openStream();
  }

  /** Tear everything down (logout). */
  stop(): void {
    this.running = false;
    this.sse?.stop();
    this.sse = null;
    if (this.offlineTimer) {
      clearTimeout(this.offlineTimer);
      this.offlineTimer = null;
    }
    if (this.tradingVpsTimer) {
      clearTimeout(this.tradingVpsTimer);
      this.tradingVpsTimer = null;
    }
    this.tradingVpsPollToken += 1;
    this.tradingVpsFailures = 0;
    this.tradingVpsDamping.clear();
    dataStore.set({ ...initialFeedState });
  }

  setPaused(paused: boolean): void {
    dataStore.set({ paused });
    if (!paused) {
      // Resuming: reconcile with a fresh snapshot so nothing was missed while frozen.
      void this.resnapshot();
    }
  }

  /** Force a snapshot reconciliation (used on resync / gap / resume). */
  async resnapshot(): Promise<void> {
    if (!this.running) return;
    dataStore.set({ resyncing: true });
    await this.loadSnapshots(false);
  }

  /** Kick an immediate trading-VPS poll (used after an admin removes a guard host). */
  refreshTradingVps(): void {
    if (!this.running) return;
    if (this.tradingVpsTimer) {
      clearTimeout(this.tradingVpsTimer);
      this.tradingVpsTimer = null;
    }
    void this.pollTradingVps();
  }

  // ---- internals ----------------------------------------------------------------------

  private async pollTradingVps(): Promise<void> {
    const token = ++this.tradingVpsPollToken;

    // Pause freezes this feed too — the pause banner promises a frozen snapshot, so the
    // trading-VPS band must not keep repainting. Keep a light timer alive to resume.
    if (dataStore.get().paused) {
      if (this.running && token === this.tradingVpsPollToken) {
        this.tradingVpsTimer = setTimeout(() => void this.pollTradingVps(), TRADING_VPS_POLL_MS);
      }
      return;
    }

    // Watchdog: abort a request that never settles so the serial poll chain cannot hang.
    const controller = new AbortController();
    const watchdog = setTimeout(() => controller.abort(), TRADING_VPS_TIMEOUT_MS);
    try {
      const response = await api.tradingVps(controller.signal);
      if (!this.running || token !== this.tradingVpsPollToken) return;
      this.tradingVpsFailures = 0;
      const serverTimeMs = Date.parse(response.server_time);
      dataStore.set({
        tradingVps: dampenTradingVpsStatuses(response.trading_vps, this.tradingVpsDamping),
        tradingVpsError: null,
        tradingVpsAt: Date.now(),
        ...(Number.isFinite(serverTimeMs)
          ? { serverTimeMs, clockOffsetMs: serverTimeMs - Date.now() }
          : {}),
      });
    } catch (error) {
      if (!this.running || token !== this.tradingVpsPollToken) return;
      this.tradingVpsFailures += 1;
      const message = controller.signal.aborted
        ? 'Trading VPS health request timed out'
        : error instanceof Error
          ? error.message
          : 'Direct VPS health is unavailable';
      dataStore.set({ tradingVpsError: message });
      if (this.isAuthError(error)) this.onAuthExpired?.();
    } finally {
      clearTimeout(watchdog);
      if (this.running && token === this.tradingVpsPollToken) {
        // Steady state polls at 1 Hz; consecutive failures back off exponentially so an
        // outage does not hammer the server, while recovery stays automatic.
        const delay =
          this.tradingVpsFailures === 0
            ? TRADING_VPS_POLL_MS
            : Math.min(TRADING_VPS_POLL_MS * 2 ** this.tradingVpsFailures, TRADING_VPS_MAX_BACKOFF_MS);
        this.tradingVpsTimer = setTimeout(() => void this.pollTradingVps(), delay);
      }
    }
  }

  private async loadSnapshots(initial: boolean): Promise<void> {
    const token = ++this.snapshotToken;
    try {
      const [fleet, accounts, alerts] = await Promise.all([
        api.fleetSummary(),
        api.accounts(),
        api.alerts(),
      ]);
      if (token !== this.snapshotToken || !this.running) return;
      dataStore.set(snapshotPatch(accounts, fleet, alerts, Date.now()));

      // Infra tables are best-effort; failure there must not block the core feed.
      try {
        const [vpsRes, termRes] = await Promise.all([api.vps(), api.terminals()]);
        if (token === this.snapshotToken && this.running) {
          dataStore.set({ vps: vpsRes.vps, terminals: termRes.terminals });
        }
      } catch {
        /* infra endpoints optional for the live feed */
      }
    } catch (err) {
      if (token !== this.snapshotToken || !this.running) return;
      const message = err instanceof Error ? err.message : 'Failed to load fleet snapshot';
      dataStore.set({ error: message, resyncing: false, loaded: initial ? false : dataStore.get().loaded });
      if (this.isAuthError(err)) {
        this.onAuthExpired?.();
      }
    }
  }

  private openStream(): void {
    this.sse?.stop();
    this.sse = new SseClient({
      url: STREAM_URL,
      getLastEventId: () => {
        const rev = dataStore.get().revision;
        return rev > 0 ? String(rev) : null;
      },
      onOpen: () => this.markLive(),
      onComment: () => this.markLive(),
      onFrame: (frame) => this.handleFrame(frame),
      onDisconnect: (err) => this.handleDisconnect(err),
      onReconnecting: () => this.markReconnecting(),
    });
    this.sse.start();
  }

  private handleFrame(frame: SseFrame): void {
    this.markLive();
    const evt = decodeFrame(frame);
    if (!evt) return;
    if (dataStore.get().paused) return; // frozen feed: ignore until resume re-snapshots

    const result = applyEvent(dataStore.get(), evt, Date.now());
    switch (result.action) {
      case 'apply':
        dataStore.set(result.patch);
        break;
      case 'stale':
        break;
      case 'gap':
      case 'resync':
        void this.resnapshot();
        break;
    }
  }

  private handleDisconnect(err: unknown): void {
    if (this.isAuthError(err)) {
      this.stop();
      this.onAuthExpired?.();
      return;
    }
    this.markReconnecting();
  }

  private markLive(): void {
    if (this.offlineTimer) {
      clearTimeout(this.offlineTimer);
      this.offlineTimer = null;
    }
    const s = dataStore.get();
    if (s.feedStatus !== 'live' || s.offAt != null) {
      dataStore.set({ feedStatus: 'live', offAt: null, lastEventAt: Date.now() });
    } else {
      dataStore.set({ lastEventAt: Date.now() });
    }
  }

  private markReconnecting(): void {
    const s = dataStore.get();
    const patch: Partial<FeedState> = {};
    if (s.feedStatus !== 'reconnecting' && s.feedStatus !== 'offline') {
      patch.feedStatus = 'reconnecting';
    }
    if (s.offAt == null) patch.offAt = Date.now();
    if (Object.keys(patch).length) dataStore.set(patch);
    // If we cannot restore within a grace window, escalate to offline.
    if (!this.offlineTimer) {
      this.offlineTimer = setTimeout(() => {
        this.offlineTimer = null;
        if (this.running && dataStore.get().feedStatus === 'reconnecting') {
          dataStore.set({ feedStatus: 'offline' });
        }
      }, 8000);
    }
  }

  private isAuthError(err: unknown): boolean {
    const status = (err as { status?: number } | null)?.status;
    return status === 401;
  }
}

export const feed = new Feed();

/** Set the browser feed status directly (used only when the whole app tears down). */
export function setFeedStatus(status: FeedStatus): void {
  dataStore.set({ feedStatus: status });
}

/** Optimistically upsert an alert after a successful ack/resolve (SSE will confirm). */
export function upsertAlertInStore(alert: import('../types/api').Alert): void {
  dataStore.set((s) => {
    const idx = s.alerts.findIndex((x) => x.id === alert.id);
    const alerts = s.alerts.slice();
    if (idx >= 0) alerts[idx] = alert;
    else alerts.unshift(alert);
    return { alerts };
  });
}
